import { isValidAddress, normalizeAddress } from './chunk-FFWAS4XO.js';
import { resolveChain, toCanonicalChainId } from './chunk-4L52XBJB.js';
import { z } from 'zod';

var ContractObservationSchema = z.object({
  observation_id: z.string(),
  chain_id: z.string(),
  subject: z.string(),
  target: z.string(),
  predicate: z.string(),
  category: z.string(),
  source: z.string(),
  assurance: z.enum(["observed", "inferred", "candidate"]),
  confidence: z.number().min(0).max(1),
  confidence_reason: z.string(),
  block_number: z.number().int().nonnegative().nullish(),
  block_hash: z.string().nullish(),
  observed_at: z.number().int().nonnegative().nullish(),
  finality: z.enum(["canonical", "safe", "finalized", "observed"]),
  value_ref: z.string(),
  output_path: z.string().default(""),
  signature: z.string().nullish(),
  selector: z.string().nullish(),
  slot: z.string().nullish(),
  storage_offset: z.number().nullish(),
  abi_source: z.string().nullish(),
  abi_fingerprint: z.string().nullish(),
  code_hash: z.string().nullish(),
  call_from: z.string().nullish(),
  params: z.array(z.unknown()).default([]),
  implementation_addresses: z.array(z.string()).default([]),
  context: z.record(z.string(), z.unknown()).default({}),
  extractor_version: z.string()
}).superRefine((fact, ctx) => {
  const family = resolveChain(fact.chain_id)?.family;
  const hash = fact.block_hash;
  const dated = fact.finality === "observed" ? fact.source === "account_state" && fact.observed_at != null : fact.block_number != null && hash != null;
  const hashValid = hash == null || (family === "near" || family === "solana" ? /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(hash) : (family === "evm" ? /^0x[0-9a-fA-F]{64}$/ : /^(?:0x)?[0-9a-fA-F]{64}$/).test(hash));
  if (!dated || !hashValid || !family || !isValidAddress(fact.subject, fact.chain_id) || !isValidAddress(fact.target, fact.chain_id)) {
    ctx.addIssue({ code: "custom", message: "Invalid chain-specific observation provenance" });
  }
});
function contractObservations(link) {
  if (link.kind !== "contract_state_reference") return [];
  const values = link.values;
  const raw = values?.contract_observations;
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    const parsed = ContractObservationSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  });
}
function isCandidateContractReference(link) {
  if (link.kind !== "contract_state_reference") return false;
  const facts = contractObservations(link);
  return facts.length === 0 || facts.every((fact) => fact.assurance === "candidate" || fact.confidence === 0 || fact.source === "storage" && fact.assurance === "inferred");
}
function contractPredicateLabel(predicate) {
  return predicate.replaceAll("_", " ").replace(/^./, (c) => c.toUpperCase());
}
function hasContractReferenceDirection(link) {
  const facts = contractObservations(link);
  if (facts.length !== 1) return false;
  const fact = facts[0];
  const chain = toCanonicalChainId(fact.chain_id) ?? fact.chain_id;
  return chain === (toCanonicalChainId(link.a_chain_id) ?? link.a_chain_id) && chain === (toCanonicalChainId(link.b_chain_id) ?? link.b_chain_id) && normalizeAddress(fact.subject, chain) === normalizeAddress(link.a_address, chain) && normalizeAddress(fact.target, chain) === normalizeAddress(link.b_address, chain);
}
function expandContractLinks(links) {
  return links.flatMap((link) => {
    const facts = contractObservations(link);
    return facts.length ? facts.map((fact) => ({
      ...link,
      a_address: fact.subject,
      b_address: fact.target,
      a_chain_id: fact.chain_id,
      b_chain_id: fact.chain_id,
      a_id: `${fact.chain_id}#${fact.subject}`,
      b_id: `${fact.chain_id}#${fact.target}`,
      confidence: fact.confidence,
      same_actor: false,
      identity_claim: false,
      cluster_id: null,
      description: `${contractPredicateLabel(fact.predicate)} \xB7 ${fact.block_number != null ? `block ${fact.block_number.toLocaleString()}` : "current state"} \xB7 ${fact.assurance}`,
      values: { ...link.values, contract_observations: [fact] }
    })) : [link];
  });
}
var ContractRelationshipOptionsSchema = z.object({
  enabled: z.boolean(),
  native_accounts: z.boolean(),
  getters: z.boolean(),
  proxies: z.boolean(),
  roles: z.boolean(),
  role_events: z.boolean(),
  role_event_lookback_blocks: z.number().int().min(1).max(1e5),
  modules: z.boolean(),
  bytecode: z.boolean(),
  inferred_storage: z.boolean(),
  drilldown: z.boolean(),
  max_depth: z.number().int().min(0).max(3),
  max_contracts: z.number().int().min(1).max(64),
  max_variables: z.number().int().min(1).max(64),
  max_observations: z.number().int().min(1).max(1024),
  max_rpc_calls: z.number().int().min(1).max(1024),
  max_lookup_calls: z.number().int().min(1).max(512),
  max_role_members: z.number().int().min(1).max(64),
  timeout_seconds: z.number().positive().max(120),
  observed_confidence: z.number().min(0).max(1),
  inferred_confidence: z.number().min(0).max(1),
  candidate_confidence: z.number().min(0).max(1)
}).partial().strict();

export { ContractObservationSchema, ContractRelationshipOptionsSchema, contractObservations, contractPredicateLabel, expandContractLinks, hasContractReferenceDirection, isCandidateContractReference };
//# sourceMappingURL=chunk-KEYGJL2X.js.map
//# sourceMappingURL=chunk-KEYGJL2X.js.map